/**
 * Mandate Store
 *
 * Zustand + Supabase store for the Mandate transition governance system.
 * Follows the exact pattern of collectionsStore.ts.
 */

import { create } from 'zustand';
import {
  generateUUID,
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import type {
  MandateTransition,
  MandateOutcomeVersion,
  MandatePriority,
  MandatePriorityRerank,
  MandateDecision,
  MandateStakeholder,
  STaRSSituation,
  MandateTransitionStatus,
  MandatePriorityStatus,
  StakeholderInfluence,
  StakeholderStance,
} from '../../types/mandate';
import {
  transitionRowToDomain,
  outcomeVersionRowToDomain,
  priorityRowToDomain,
  rerankRowToDomain,
  decisionRowToDomain,
  stakeholderRowToDomain,
} from '../../types/mandate';
import {
  activateMandateSpace,
  bootstrapMandateIndividualSpace,
  createMandateStackRankingSession,
  promoteMandatePriorityToItem,
  promoteMandateStakeholderToItem,
  syncMandateDecisionToPlatform,
} from '../../lib/mandateBridgeService';

// =============================================================================
// Input types for create/update operations
// =============================================================================

export interface CreateTransitionInput {
  title?: string;
  organization: string;
  role: string;
  startDate?: string;
  endDate?: string;
  situationType?: STaRSSituation;
  situationNotes?: string;
  status?: MandateTransitionStatus;
}

export interface UpdateTransitionInput {
  title?: string;
  organization?: string;
  role?: string;
  startDate?: string;
  endDate?: string;
  situationType?: STaRSSituation | null;
  situationNotes?: string | null;
  status?: MandateTransitionStatus;
}

export interface AddPriorityInput {
  title: string;
  description?: string;
  rank?: number;
  rationale?: string;
}

export interface UpdatePriorityInput {
  title?: string;
  description?: string | null;
  rank?: number;
  rationale?: string | null;
  status?: MandatePriorityStatus;
}

export interface AddDecisionInput {
  title: string;
  description?: string;
  rationale: string;
  signal?: string;
  peopleConsulted?: string;
  outcome?: string;
}

export interface UpdateDecisionInput {
  title?: string;
  description?: string | null;
  rationale?: string;
  signal?: string | null;
  peopleConsulted?: string | null;
  outcome?: string | null;
}

export interface AddStakeholderInput {
  name: string;
  role?: string;
  influence?: StakeholderInfluence;
  stance?: StakeholderStance;
  trustGaps?: string;
  notes?: string;
}

export interface UpdateStakeholderInput {
  name?: string;
  role?: string | null;
  influence?: StakeholderInfluence | null;
  stance?: StakeholderStance | null;
  trustGaps?: string | null;
  notes?: string | null;
}

// =============================================================================
// Store State
// =============================================================================

interface MandateState {
  transition: MandateTransition | null;
  outcomeVersions: MandateOutcomeVersion[];
  priorities: MandatePriority[];
  reranks: MandatePriorityRerank[];
  decisions: MandateDecision[];
  stakeholders: MandateStakeholder[];
  syncState: SyncState;

  // Computed
  currentOutcome: MandateOutcomeVersion | null;

  // Actions
  fetchAll: () => Promise<void>;
  createTransition: (data: CreateTransitionInput) => Promise<MandateTransition | null>;
  updateTransition: (updates: UpdateTransitionInput) => Promise<void>;

  setOutcomeHypothesis: (hypothesis: string, revisionReason?: string) => Promise<void>;

  addPriority: (data: AddPriorityInput) => Promise<MandatePriority | null>;
  updatePriority: (id: string, updates: UpdatePriorityInput) => Promise<void>;
  rerankPriority: (priorityId: string, newRank: number, signal: string) => Promise<void>;
  deletePriority: (id: string) => Promise<void>;

  addDecision: (data: AddDecisionInput) => Promise<MandateDecision | null>;
  updateDecision: (id: string, updates: UpdateDecisionInput) => Promise<void>;
  deleteDecision: (id: string) => Promise<void>;

  addStakeholder: (data: AddStakeholderInput) => Promise<MandateStakeholder | null>;
  updateStakeholder: (id: string, updates: UpdateStakeholderInput) => Promise<void>;
  deleteStakeholder: (id: string) => Promise<void>;

  /** Activate mandate Individual Space in spaceStore. */
  activateSpace: () => Promise<void>;
  /** Promote priority → platform item (origin_module = mandate). */
  promotePriorityToItem: (priorityId: string, userId: string) => Promise<{ itemId: string | null; error?: string }>;
  /** Mirror decision → platform decisions table. */
  mirrorDecisionToPlatform: (decisionId: string) => Promise<{ platformDecisionId: string | null; error?: string }>;
  /** Promote stakeholder → people-type item. */
  promoteStakeholderToItem: (stakeholderId: string, userId: string) => Promise<{ itemId: string | null; error?: string }>;
  /** Launch isolated stack-ranking session from active priorities. */
  launchStackRankingSession: (userId: string) => Promise<{ sessionId: string | null; error?: string }>;
}

// =============================================================================
// Store Implementation
// =============================================================================

export const useMandateStore = create<MandateState>((set, get) => ({
  transition: null,
  outcomeVersions: [],
  priorities: [],
  reranks: [],
  decisions: [],
  stakeholders: [],
  syncState: createInitialSyncState(),
  currentOutcome: null,

  // ---------------------------------------------------------------------------
  // fetchAll
  // ---------------------------------------------------------------------------
  fetchAll: async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set((s) => ({ syncState: { ...s.syncState, isLoading: true } }));

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set((s) => ({ syncState: { ...s.syncState, isLoading: false } }));
        return;
      }

      // Fetch the active/current transition for this user
      const { data: transitionRows, error: transErr } = await supabase
        .from('mandate_transitions')
        .select('*')
        .eq('owner_id', user.id)
        .not('status', 'eq', 'archived')
        .order('created_at', { ascending: false })
        .limit(1);

      if (transErr) {
        console.error('[MandateStore] Failed to fetch transition:', transErr.message);
        set((s) => ({ syncState: { ...s.syncState, isLoading: false } }));
        return;
      }

      const transitionRow = transitionRows?.[0];
      if (!transitionRow) {
        set((s) => ({
          transition: null,
          outcomeVersions: [],
          priorities: [],
          reranks: [],
          decisions: [],
          stakeholders: [],
          currentOutcome: null,
          syncState: { ...s.syncState, isLoading: false, lastSyncAt: new Date() },
        }));
        return;
      }

      const transition = transitionRowToDomain(transitionRow as Record<string, unknown>);
      const tid = transition.id;

      const [outcomesRes, prioritiesRes, reranksRes, decisionsRes, stakeholdersRes] =
        await Promise.all([
          supabase
            .from('mandate_outcome_versions')
            .select('*')
            .eq('transition_id', tid)
            .order('version', { ascending: false }),
          supabase
            .from('mandate_priorities')
            .select('*')
            .eq('transition_id', tid)
            .order('rank', { ascending: true }),
          supabase
            .from('mandate_priority_reranks')
            .select('*')
            .eq('transition_id', tid)
            .order('occurred_at', { ascending: false }),
          supabase
            .from('mandate_decisions')
            .select('*')
            .eq('transition_id', tid)
            .order('created_at', { ascending: false }),
          supabase
            .from('mandate_stakeholders')
            .select('*')
            .eq('transition_id', tid)
            .order('created_at', { ascending: true }),
        ]);

      const outcomeVersions = ((outcomesRes.data as Record<string, unknown>[] | null) ?? []).map(
        outcomeVersionRowToDomain
      );

      set({
        transition,
        outcomeVersions,
        priorities: ((prioritiesRes.data as Record<string, unknown>[] | null) ?? []).map(
          priorityRowToDomain
        ),
        reranks: ((reranksRes.data as Record<string, unknown>[] | null) ?? []).map(
          rerankRowToDomain
        ),
        decisions: ((decisionsRes.data as Record<string, unknown>[] | null) ?? []).map(
          decisionRowToDomain
        ),
        stakeholders: ((stakeholdersRes.data as Record<string, unknown>[] | null) ?? []).map(
          stakeholderRowToDomain
        ),
        currentOutcome: outcomeVersions.find((v) => v.isCurrent) ?? null,
        syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date() },
      });
    } catch (err) {
      console.error('[MandateStore] fetchAll failed:', err);
      set((s) => ({ syncState: { ...s.syncState, isLoading: false } }));
    }
  },

  // ---------------------------------------------------------------------------
  // createTransition
  // ---------------------------------------------------------------------------
  createTransition: async (data) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const id = generateUUID();
      const now = new Date().toISOString();
      const row = {
        id,
        workspace_id: workspaceId ?? id, // fallback to uuid if no workspace
        owner_id: user.id,
        title: data.title ?? `${data.role} @ ${data.organization}`,
        organization: data.organization,
        role: data.role,
        start_date: data.startDate ?? null,
        end_date: data.endDate ?? null,
        situation_type: data.situationType ?? null,
        situation_notes: data.situationNotes ?? null,
        status: data.status ?? 'setup',
        space_id: null,
        context_id: null,
        created_at: now,
        updated_at: now,
      };

      const { error } = await supabase.from('mandate_transitions').insert(row);
      if (error) {
        console.error('[MandateStore] Failed to create transition:', error.message);
        return null;
      }

      let transition = transitionRowToDomain(row as Record<string, unknown>);

      const boot = await bootstrapMandateIndividualSpace(transition, user.id);
      if (boot.spaceId || boot.contextId) {
        transition = {
          ...transition,
          spaceId: boot.spaceId ?? transition.spaceId,
          contextId: boot.contextId ?? transition.contextId,
        };
      }

      set({ transition });
      return transition;
    } catch (err) {
      console.error('[MandateStore] createTransition failed:', err);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // updateTransition
  // ---------------------------------------------------------------------------
  updateTransition: async (updates) => {
    const supabase = getSafeSupabaseClient();
    const { transition } = get();
    if (!supabase || !transition) return;

    try {
      const now = new Date().toISOString();
      const dbUpdates: Record<string, unknown> = { updated_at: now };
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.organization !== undefined) dbUpdates.organization = updates.organization;
      if (updates.role !== undefined) dbUpdates.role = updates.role;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
      if ('situationType' in updates) dbUpdates.situation_type = updates.situationType;
      if ('situationNotes' in updates) dbUpdates.situation_notes = updates.situationNotes;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      // Optimistic update
      set({ transition: { ...transition, ...updates, updatedAt: now } });

      const { error } = await supabase
        .from('mandate_transitions')
        .update(dbUpdates)
        .eq('id', transition.id);

      if (error) {
        console.error('[MandateStore] Failed to update transition:', error.message);
        set({ transition });
        dispatchStoreError('Mandate', 'update transition', error.message);
      }
    } catch (err) {
      console.error('[MandateStore] updateTransition failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // setOutcomeHypothesis
  // ---------------------------------------------------------------------------
  setOutcomeHypothesis: async (hypothesis, revisionReason) => {
    const supabase = getSafeSupabaseClient();
    const { transition, outcomeVersions } = get();
    if (!supabase || !transition) return;

    try {
      const currentVersion = outcomeVersions.find((v) => v.isCurrent);
      const nextVersionNumber = currentVersion ? currentVersion.version + 1 : 1;
      const newId = generateUUID();
      const now = new Date().toISOString();

      // Mark previous version as not current
      if (currentVersion) {
        await supabase
          .from('mandate_outcome_versions')
          .update({ is_current: false })
          .eq('id', currentVersion.id);
      }

      const newRow = {
        id: newId,
        transition_id: transition.id,
        hypothesis,
        version: nextVersionNumber,
        revision_reason: revisionReason ?? null,
        revised_from_id: currentVersion?.id ?? null,
        is_current: true,
        created_at: now,
      };

      const { error } = await supabase.from('mandate_outcome_versions').insert(newRow);
      if (error) {
        console.error('[MandateStore] Failed to set outcome hypothesis:', error.message);
        // Revert the is_current update
        if (currentVersion) {
          await supabase
            .from('mandate_outcome_versions')
            .update({ is_current: true })
            .eq('id', currentVersion.id);
        }
        return;
      }

      const newVersion = outcomeVersionRowToDomain(newRow as Record<string, unknown>);
      const updatedVersions = [
        newVersion,
        ...outcomeVersions.map((v) => (v.isCurrent ? { ...v, isCurrent: false } : v)),
      ];

      set({ outcomeVersions: updatedVersions, currentOutcome: newVersion });
    } catch (err) {
      console.error('[MandateStore] setOutcomeHypothesis failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // addPriority
  // ---------------------------------------------------------------------------
  addPriority: async (data) => {
    const supabase = getSafeSupabaseClient();
    const { transition, priorities } = get();
    if (!supabase || !transition) return null;

    try {
      const id = generateUUID();
      const now = new Date().toISOString();
      const rank = data.rank ?? priorities.length + 1;

      const row = {
        id,
        transition_id: transition.id,
        title: data.title,
        description: data.description ?? null,
        rank,
        rationale: data.rationale ?? null,
        status: 'active',
        created_at: now,
        updated_at: now,
      };

      const priority = priorityRowToDomain(row as Record<string, unknown>);
      set((s) => ({ priorities: [...s.priorities, priority].sort((a, b) => a.rank - b.rank) }));

      const { error } = await supabase.from('mandate_priorities').insert(row);
      if (error) {
        console.error('[MandateStore] Failed to add priority:', error.message);
        set((s) => ({ priorities: s.priorities.filter((p) => p.id !== id) }));
        return null;
      }

      return priority;
    } catch (err) {
      console.error('[MandateStore] addPriority failed:', err);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // updatePriority
  // ---------------------------------------------------------------------------
  updatePriority: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const now = new Date().toISOString();
      set((s) => ({
        priorities: s.priorities.map((p) =>
          p.id === id ? { ...p, ...updates, updatedAt: now } : p
        ),
      }));

      const dbUpdates: Record<string, unknown> = { updated_at: now };
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if ('description' in updates) dbUpdates.description = updates.description;
      if (updates.rank !== undefined) dbUpdates.rank = updates.rank;
      if ('rationale' in updates) dbUpdates.rationale = updates.rationale;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      const { error } = await supabase.from('mandate_priorities').update(dbUpdates).eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to update priority:', error.message);
        dispatchStoreError('Mandate', 'update priority', error.message);
      }
    } catch (err) {
      console.error('[MandateStore] updatePriority failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // rerankPriority
  // ---------------------------------------------------------------------------
  rerankPriority: async (priorityId, newRank, signal) => {
    const supabase = getSafeSupabaseClient();
    const { transition, priorities } = get();
    if (!supabase || !transition) return;

    try {
      const target = priorities.find((p) => p.id === priorityId);
      if (!target) return;

      const previousRank = target.rank;
      const clampedRank = Math.max(1, Math.min(newRank, priorities.length));

      // Re-normalize ranks: shift others out of the way
      const reranked = priorities
        .map((p) => {
          if (p.id === priorityId) return { ...p, rank: clampedRank };
          if (previousRank < clampedRank) {
            // Moving down: shift items between old and new up by 1
            if (p.rank > previousRank && p.rank <= clampedRank) return { ...p, rank: p.rank - 1 };
          } else {
            // Moving up: shift items between new and old down by 1
            if (p.rank >= clampedRank && p.rank < previousRank) return { ...p, rank: p.rank + 1 };
          }
          return p;
        })
        .sort((a, b) => a.rank - b.rank);

      set({ priorities: reranked });

      // Create the rerank record
      const rerankId = generateUUID();
      const now = new Date().toISOString();
      const rerankRow = {
        id: rerankId,
        transition_id: transition.id,
        priority_id: priorityId,
        previous_rank: previousRank,
        new_rank: clampedRank,
        signal,
        occurred_at: now,
      };

      // Batch DB updates
      const updates = reranked.map((p) =>
        supabase
          .from('mandate_priorities')
          .update({ rank: p.rank, updated_at: now })
          .eq('id', p.id)
      );
      const rerankInsert = supabase.from('mandate_priority_reranks').insert(rerankRow);

      const results = await Promise.all([...updates, rerankInsert]);
      const anyError = results.some((r) => r.error);
      if (anyError) {
        console.error('[MandateStore] rerankPriority had errors — refetching');
        get().fetchAll();
        return;
      }

      const newRerank = rerankRowToDomain(rerankRow as Record<string, unknown>);
      set((s) => ({ reranks: [newRerank, ...s.reranks] }));
    } catch (err) {
      console.error('[MandateStore] rerankPriority failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // deletePriority
  // ---------------------------------------------------------------------------
  deletePriority: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const prev = get().priorities;
      set((s) => ({ priorities: s.priorities.filter((p) => p.id !== id) }));

      const { error } = await supabase.from('mandate_priorities').delete().eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to delete priority:', error.message);
        set({ priorities: prev });
      }
    } catch (err) {
      console.error('[MandateStore] deletePriority failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // addDecision
  // ---------------------------------------------------------------------------
  addDecision: async (data) => {
    const supabase = getSafeSupabaseClient();
    const { transition } = get();
    if (!supabase || !transition) return null;

    try {
      const id = generateUUID();
      const now = new Date().toISOString();

      const row = {
        id,
        transition_id: transition.id,
        title: data.title,
        description: data.description ?? null,
        rationale: data.rationale,
        signal: data.signal ?? null,
        people_consulted: data.peopleConsulted ?? null,
        outcome: data.outcome ?? null,
        created_at: now,
        updated_at: now,
      };

      const decision = decisionRowToDomain(row as Record<string, unknown>);
      set((s) => ({ decisions: [decision, ...s.decisions] }));

      const { error } = await supabase.from('mandate_decisions').insert(row);
      if (error) {
        console.error('[MandateStore] Failed to add decision:', error.message);
        set((s) => ({ decisions: s.decisions.filter((d) => d.id !== id) }));
        return null;
      }

      const mirror = await syncMandateDecisionToPlatform(decision, transition);
      if (mirror.platformDecisionId) {
        const linked = { ...decision, platformDecisionId: mirror.platformDecisionId };
        set((s) => ({
          decisions: s.decisions.map((d) => (d.id === id ? linked : d)),
        }));
        return linked;
      }

      return decision;
    } catch (err) {
      console.error('[MandateStore] addDecision failed:', err);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // updateDecision
  // ---------------------------------------------------------------------------
  updateDecision: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const now = new Date().toISOString();
      set((s) => ({
        decisions: s.decisions.map((d) =>
          d.id === id ? { ...d, ...updates, updatedAt: now } : d
        ),
      }));

      const dbUpdates: Record<string, unknown> = { updated_at: now };
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if ('description' in updates) dbUpdates.description = updates.description;
      if (updates.rationale !== undefined) dbUpdates.rationale = updates.rationale;
      if ('signal' in updates) dbUpdates.signal = updates.signal;
      if ('peopleConsulted' in updates) dbUpdates.people_consulted = updates.peopleConsulted;
      if ('outcome' in updates) dbUpdates.outcome = updates.outcome;

      const { error } = await supabase.from('mandate_decisions').update(dbUpdates).eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to update decision:', error.message);
        dispatchStoreError('Mandate', 'update decision', error.message);
      }
    } catch (err) {
      console.error('[MandateStore] updateDecision failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // deleteDecision
  // ---------------------------------------------------------------------------
  deleteDecision: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const prev = get().decisions;
      set((s) => ({ decisions: s.decisions.filter((d) => d.id !== id) }));

      const { error } = await supabase.from('mandate_decisions').delete().eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to delete decision:', error.message);
        set({ decisions: prev });
      }
    } catch (err) {
      console.error('[MandateStore] deleteDecision failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // addStakeholder
  // ---------------------------------------------------------------------------
  addStakeholder: async (data) => {
    const supabase = getSafeSupabaseClient();
    const { transition } = get();
    if (!supabase || !transition) return null;

    try {
      const id = generateUUID();
      const now = new Date().toISOString();

      const row = {
        id,
        transition_id: transition.id,
        name: data.name,
        role: data.role ?? null,
        influence: data.influence ?? null,
        stance: data.stance ?? null,
        trust_gaps: data.trustGaps ?? null,
        notes: data.notes ?? null,
        created_at: now,
      };

      const stakeholder = stakeholderRowToDomain(row as Record<string, unknown>);
      set((s) => ({ stakeholders: [...s.stakeholders, stakeholder] }));

      const { error } = await supabase.from('mandate_stakeholders').insert(row);
      if (error) {
        console.error('[MandateStore] Failed to add stakeholder:', error.message);
        set((s) => ({ stakeholders: s.stakeholders.filter((sh) => sh.id !== id) }));
        return null;
      }

      return stakeholder;
    } catch (err) {
      console.error('[MandateStore] addStakeholder failed:', err);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // updateStakeholder
  // ---------------------------------------------------------------------------
  updateStakeholder: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      set((s) => ({
        stakeholders: s.stakeholders.map((sh) => (sh.id === id ? { ...sh, ...updates } : sh)),
      }));

      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if ('role' in updates) dbUpdates.role = updates.role;
      if ('influence' in updates) dbUpdates.influence = updates.influence;
      if ('stance' in updates) dbUpdates.stance = updates.stance;
      if ('trustGaps' in updates) dbUpdates.trust_gaps = updates.trustGaps;
      if ('notes' in updates) dbUpdates.notes = updates.notes;

      const { error } = await supabase.from('mandate_stakeholders').update(dbUpdates).eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to update stakeholder:', error.message);
        dispatchStoreError('Mandate', 'update stakeholder', error.message);
      }
    } catch (err) {
      console.error('[MandateStore] updateStakeholder failed:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // deleteStakeholder
  // ---------------------------------------------------------------------------
  deleteStakeholder: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const prev = get().stakeholders;
      set((s) => ({ stakeholders: s.stakeholders.filter((sh) => sh.id !== id) }));

      const { error } = await supabase.from('mandate_stakeholders').delete().eq('id', id);
      if (error) {
        console.error('[MandateStore] Failed to delete stakeholder:', error.message);
        set({ stakeholders: prev });
      }
    } catch (err) {
      console.error('[MandateStore] deleteStakeholder failed:', err);
    }
  },

  activateSpace: async () => {
    const { transition } = get();
    if (!transition) return;
    await activateMandateSpace(transition);
  },

  promotePriorityToItem: async (priorityId, userId) => {
    const { transition, priorities } = get();
    if (!transition) return { itemId: null, error: 'No active transition' };
    const priority = priorities.find((p) => p.id === priorityId);
    if (!priority) return { itemId: null, error: 'Priority not found' };

    const result = await promoteMandatePriorityToItem(priority, transition, userId);
    if (result.itemId) {
      set((s) => ({
        priorities: s.priorities.map((p) =>
          p.id === priorityId ? { ...p, platformItemId: result.itemId } : p
        ),
      }));
    }
    return result;
  },

  mirrorDecisionToPlatform: async (decisionId) => {
    const { transition, decisions } = get();
    if (!transition) return { platformDecisionId: null, error: 'No active transition' };
    const decision = decisions.find((d) => d.id === decisionId);
    if (!decision) return { platformDecisionId: null, error: 'Decision not found' };

    const result = await syncMandateDecisionToPlatform(decision, transition);
    if (result.platformDecisionId) {
      set((s) => ({
        decisions: s.decisions.map((d) =>
          d.id === decisionId ? { ...d, platformDecisionId: result.platformDecisionId } : d
        ),
      }));
    }
    return result;
  },

  promoteStakeholderToItem: async (stakeholderId, userId) => {
    const { transition, stakeholders } = get();
    if (!transition) return { itemId: null, error: 'No active transition' };
    const stakeholder = stakeholders.find((sh) => sh.id === stakeholderId);
    if (!stakeholder) return { itemId: null, error: 'Stakeholder not found' };

    const result = await promoteMandateStakeholderToItem(stakeholder, transition, userId);
    if (result.itemId) {
      set((s) => ({
        stakeholders: s.stakeholders.map((sh) =>
          sh.id === stakeholderId ? { ...sh, platformItemId: result.itemId } : sh
        ),
      }));
    }
    return result;
  },

  launchStackRankingSession: async (userId) => {
    const { transition, priorities } = get();
    if (!transition) return { sessionId: null, error: 'No active transition' };
    return createMandateStackRankingSession(priorities, transition, userId);
  },
}));
